import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useState } from 'react';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  payrollId: string;
  hideModal: () => void;
  onSuccess?: () => void;
};

const ConfirmApprovalPayrollModal = ({
  payrollId,
  hideModal,
  onSuccess,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprovePreviewPayroll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      await axiosRubyClient.post(`/api/payrolls/${payrollId}/approve`);
      hideModal();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error approving payroll:', err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'There was an error approving the payroll',
      );
    } finally {
      setIsLoading(false);
    }
  }, [payrollId, hideModal, onSuccess]);

  return (
    <Modal
      disableClickout
      size="sm"
      title="Are you sure you want to approve this payroll?"
      onRequestClose={hideModal}
    >
      <Card.Section>
        {error ? (
          <>
            <Alert
              description={error}
              icon={faExclamationTriangle}
              status="danger"
              title="An error has occurred"
            />
            <Stack css={{ marginTop: '10px' }} gap={10} justify="end">
              <Button
                a11yLabel="close modal"
                id="ok-btn"
                label="Ok"
                status="danger"
                type="button"
                onClick={hideModal}
              />
            </Stack>
          </>
        ) : (
          <>
            <Body>After approving this payroll it cannot be deleted.</Body>
            <Stack css={{ marginTop: '10px' }} gap={10} justify="end">
              <Button
                a11yLabel="deny"
                appearance="outline"
                id="cancel-btn"
                label="Cancel"
                status="danger"
                type="button"
                onClick={hideModal}
              />

              <Button
                a11yLabel="accept"
                id="approve-btn"
                isLoading={isLoading}
                label="Approve"
                type="submit"
                onClick={handleApprovePreviewPayroll}
              />
            </Stack>
          </>
        )}
      </Card.Section>
    </Modal>
  );
};

export default ConfirmApprovalPayrollModal;
